/**
 * MegaTree Friendly SEO — universal collection filter engine
 * Boot via snippets/megatree-filters-boot.liquid (one include in layout/theme.liquid).
 * No theme filter/sidebar Liquid surgery required.
 *
 * Friendly SEO → /collections/{handle}/{slug}[+{slug}] path URLs.
 * Everything Shopify cannot filter by path (price, availability) and every value
 * MegaTree has no entry for stays a normal ?filter.* query parameter.
 *
 * Friendly values are resolved in this order:
 *   1. explicit app flag  (friendly_seo / friendlySeo / FriendlySeo / friendly-seo,
 *                          or the same keys nested under .seo)
 *   2. fallback: a canonical_url that is a child of THIS collection url
 * Set window.ShopifyMegatree.friendlyFallback = false to disable step 2.
 */
(function () {
  'use strict';

  window.openCustomFacetLink = function openCustomFacetLink(event, pathname) {
    event.preventDefault();
    event.stopPropagation();

    const facetLink = new URL(window.location.href);
    facetLink.pathname = pathname;
    facetLink.search = '';
    window.location.href = facetLink.href;
  };

  const api = (window.ShopifyMegatree = window.ShopifyMegatree || {});

  const FILTER_FORM_SELECTOR = [
    'form#desktop-filters-form',
    'form#mobile-collection-filters-form',
    'form#MegatreeFiltersForm',
    'form[data-collection-filters-form]',
    'collection-filters-form form',
    'facet-filters-form form[id*="Facet"][id*="Form"]',
    'form[id*="FacetFilters"]',
    'form[id*="FiltersForm"]',
    'form[id*="filters-form"]',
    'form.filter-form',
    'form.collection__filters',
    'form.collection-drawer__inner'
  ].join(', ');

  const SIDEBAR_ROOT_SELECTOR = [
    '.collection__sidebar',
    '.collection__filter-group-list',
    '#desktop-filters-form',
    '.facets-container',
    'facet-filters-form',
    '[data-collection-sidebar]',
    '.collection-filters',
    '#FacetsWrapperDesktop',
    '#mobile-collection-filters'
  ].join(', ');

  const H1_SELECTOR =
    '.collection-image-with-title h1.hero__title, h1.collection-hero__title, h1.collection__title, h1.hero__title, .collection-hero h1';

  const PAIR_KEY = String.fromCharCode(0);

  let navigating = false;
  let hydrateTimer = null;
  let hydrating = false;
  let suppressHydrate = false;
  let h1SyncTimer = null;

  // ---------------------------------------------------------------------------
  // Text helpers
  // ---------------------------------------------------------------------------

  function normalizeText(value) {
    return String(value || '')
      .normalize('NFKC')
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]+/g, ' ')
      .trim();
  }

  function slugify(value) {
    return normalizeText(value)
      .toLowerCase()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-+/g, '-');
  }

  function valuesMatch(a, b) {
    if (a === b) return true;
    if (normalizeText(a) === normalizeText(b)) return true;
    return Boolean(slugify(a)) && slugify(a) === slugify(b);
  }

  function tagsInclude(tags, value) {
    if (!Array.isArray(tags) || !tags.length) return false;
    return tags.some((tag) => valuesMatch(tag, value));
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function pairKey(name, value) {
    return name + PAIR_KEY + value;
  }

  function upsertPair(list, seen, name, value) {
    const key = pairKey(name, value);
    if (seen.has(key)) return;
    seen.add(key);
    list.push([name, value]);
  }

  function removePair(list, name, value) {
    for (let i = list.length - 1; i >= 0; i--) {
      if (list[i][0] === name && valuesMatch(list[i][1], value)) list.splice(i, 1);
    }
  }

  function removePairsByName(list, name, seen) {
    for (let i = list.length - 1; i >= 0; i--) {
      if (list[i][0] !== name) continue;
      if (seen) seen.delete(pairKey(list[i][0], list[i][1]));
      list.splice(i, 1);
    }
  }

  // ---------------------------------------------------------------------------
  // Metafield access + Friendly SEO inference
  // ---------------------------------------------------------------------------

  function parseMetaEntry(entry) {
    if (!entry) return null;
    if (typeof entry === 'string') {
      try {
        entry = JSON.parse(entry);
      } catch (e) {
        return null;
      }
    }
    if (typeof entry !== 'object' || Array.isArray(entry)) return null;
    return entry;
  }

  function isTruthyFlag(v) {
    if (v === true || v === 1) return true;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === '1' || s === 'true' || s === 'yes' || s === 'on';
    }
    return false;
  }

  function isFalsyFlag(v) {
    if (v === false || v === 0) return true;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === '' || s === '0' || s === 'false' || s === 'no' || s === 'off';
    }
    return false;
  }

  function collectionBaseUrl() {
    return String(api.url || '').replace(/\/+$/, '') || '/';
  }

  /**
   * The slug a canonical_url contributes to the path, or '' when the canonical
   * is missing / not a child of this collection. Never throws.
   */
  function canonicalPathSlug(canonical) {
    if (!canonical) return '';
    try {
      const path = new URL(canonical, window.location.origin).pathname.replace(/\/+$/, '');
      const base = collectionBaseUrl();
      if (!base || base === '/' || !path.toLowerCase().startsWith(`${base.toLowerCase()}/`)) return '';
      const slug = decodeURIComponent(path.slice(base.length + 1).split('/')[0] || '').trim();
      return slug;
    } catch (e) {
      return '';
    }
  }

  /** true / false when the app set the flag, null when the app set nothing. */
  function explicitFriendlyFlag(rawEntry) {
    const entry = parseMetaEntry(rawEntry);
    if (!entry) return null;

    const nested = entry.seo && typeof entry.seo === 'object' ? entry.seo : null;
    const candidates = [
      entry.friendly_seo,
      entry.friendlySeo,
      entry.FriendlySeo,
      entry['friendly-seo'],
      nested && nested.friendly_seo,
      nested && nested.friendlySeo
    ];

    for (const v of candidates) {
      if (v === undefined || v === null) continue;
      if (isFalsyFlag(v)) return false;
      if (isTruthyFlag(v)) return true;
    }
    return null;
  }

  function friendlyFallbackEnabled() {
    return api.friendlyFallback !== false;
  }

  /**
   * Friendly SEO for one metafield entry.
   * An explicit flag always wins (including an explicit "off").
   * With no flag at all, a usable canonical_url is treated as intent.
   */
  function isFriendlyEntry(rawEntry) {
    const explicit = explicitFriendlyFlag(rawEntry);
    if (explicit !== null) return explicit;
    if (!friendlyFallbackEnabled()) return false;
    const entry = parseMetaEntry(rawEntry);
    if (!entry) return false;
    return Boolean(canonicalPathSlug(entry.canonical_url));
  }

  function stripMetaKeyNoise(key) {
    return String(key || '')
      .replace(/^filter\.p\.m\./i, '')
      .replace(/^filter\.(p|v)\./i, '')
      .replace(/^group\./i, '')
      .replace(/^brands[z]?:/i, '')
      .replace(/^brands\//i, '')
      .replace(/^brand[z]?:/i, '')
      .replace(/^brand\//i, '')
      .replace(/^tags?:/i, '')
      .replace(/^tag\//i, '')
      .trim();
  }

  /**
   * The MegaTree metafield flattened to one entry per filter value.
   * Handles the flat shape ({"Babolat": {...}}) and one level of grouping
   * ({"Brands": {"Babolat": {...}}}).
   */
  function getMetaObject() {
    let meta = api.meta;

    if (typeof meta === 'string') {
      try {
        meta = JSON.parse(meta);
      } catch (e) {
        meta = {};
      }
      api.meta = meta;
    }

    if (!meta || typeof meta !== 'object') meta = {};
    if (Array.isArray(meta)) meta = arrayToObject(meta);
    api.meta = meta;

    if (api._metaSource === meta) return api._metaFlat;

    const flat = Object.assign({}, meta);

    Object.keys(meta).forEach((key) => {
      const entry = parseMetaEntry(meta[key]);
      if (!entry) return;

      const looksLikeGroup =
        explicitFriendlyFlag(entry) === null &&
        entry.h1 == null &&
        entry.meta_title == null &&
        entry.canonical_url == null &&
        Object.values(entry).some((child) => parseMetaEntry(child));
      if (!looksLikeGroup) return;

      Object.keys(entry).forEach((childKey) => {
        const child = parseMetaEntry(entry[childKey]);
        if (!child) return;
        if (flat[childKey] == null) flat[childKey] = child;
        const compound = `${key}/${childKey}`;
        if (flat[compound] == null) flat[compound] = child;
      });
    });

    api._metaSource = meta;
    api._metaFlat = flat;
    api._metaIndex = null;
    api._friendlyIndex = null;
    api._metaMiss = null;
    api._friendlyMiss = null;
    return flat;
  }

  function arrayToObject(list) {
    const mapped = {};
    list.forEach((item, i) => {
      const entry = parseMetaEntry(item);
      if (!entry) return;
      const key = entry.value || entry.tag || entry.name || entry.handle || entry.slug || String(i);
      mapped[key] = entry;
    });
    return mapped;
  }

  // ---------------------------------------------------------------------------
  // Value → metafield key resolution
  // ---------------------------------------------------------------------------

  function addIndexAlias(index, alias, key) {
    const s = slugify(alias);
    if (s && !index.has(s)) index.set(s, key);
    const stripped = slugify(stripMetaKeyNoise(alias));
    if (stripped && !index.has(stripped)) index.set(stripped, key);
  }

  function entryAliases(key, entry) {
    const aliases = [key, stripMetaKeyNoise(key), entry.name, entry.slug, entry.handle, entry.tag, entry.value, entry.label];
    String(key)
      .split(/[/:]/)
      .forEach((part) => aliases.push(part));
    const dotParts = String(key).split('.');
    if (dotParts.length > 1) aliases.push(dotParts[dotParts.length - 1]);
    return aliases.filter((a) => a != null && a !== '');
  }

  function rebuildIndexes() {
    const meta = getMetaObject();
    const friendly = new Map();
    const all = new Map();

    Object.keys(meta).forEach((key) => {
      const entry = parseMetaEntry(meta[key]);
      if (!entry) return;
      const aliases = entryAliases(key, entry);
      aliases.forEach((alias) => addIndexAlias(all, alias, key));
      if (!isFriendlyEntry(entry)) return;
      aliases.forEach((alias) => addIndexAlias(friendly, alias, key));
    });

    api._friendlyIndex = friendly;
    api._metaIndex = all;
    api._metaMiss = new Set();
    api._friendlyMiss = new Set();
    return { friendly, all };
  }

  function keyMatchesWant(key, want) {
    const slug = slugify(key);
    if (!slug || !want) return false;
    if (slug === want) return true;
    const stripped = slugify(stripMetaKeyNoise(key));
    if (stripped === want) return true;
    if (slug.endsWith(`-${want}`) || stripped.endsWith(`-${want}`)) {
      return /^(filter-p-m-)?(group-)?(brands?|tags?)-/.test(slug) || /^(brands?|tags?)-/.test(stripped);
    }
    return false;
  }

  function metaEntryMatches(key, entry, value) {
    const want = slugify(value);
    if (!want) return false;

    const keyTail = String(key || '').includes('/') ? String(key).split('/').pop() : key;
    const candidates = [
      key,
      keyTail,
      stripMetaKeyNoise(key),
      stripMetaKeyNoise(keyTail),
      entry && entry.name,
      entry && entry.slug,
      entry && entry.handle,
      entry && entry.tag,
      entry && entry.value,
      entry && entry.label
    ];

    return candidates.some((candidate) => {
      if (candidate == null || candidate === '') return false;
      return valuesMatch(candidate, value) || slugify(candidate) === want;
    });
  }

  function cleanLookupValue(value) {
    return normalizeText(String(value == null ? '' : value).replace(/&amp;/g, '&').replace(/&nbsp;/g, ' '));
  }

  function resolveKeyFromValue(value, onlyFriendly) {
    const meta = getMetaObject();
    const raw = cleanLookupValue(value);
    if (!raw) return null;
    const want = slugify(raw);

    let index = onlyFriendly ? api._friendlyIndex : api._metaIndex;
    if (!index) index = rebuildIndexes()[onlyFriendly ? 'friendly' : 'all'];

    if (index.has(want)) return index.get(want);

    const missKey = onlyFriendly ? '_friendlyMiss' : '_metaMiss';
    const misses = api[missKey] || (api[missKey] = new Set());
    if (misses.has(want)) return null;

    for (const tryKey of [raw, value, want, stripMetaKeyNoise(raw)]) {
      if (!tryKey || meta[tryKey] == null) continue;
      if (!onlyFriendly || isFriendlyEntry(meta[tryKey])) return tryKey;
    }

    for (const key of Object.keys(meta)) {
      const entry = parseMetaEntry(meta[key]);
      if (onlyFriendly && !isFriendlyEntry(entry)) continue;
      if (slugify(key) === want || keyMatchesWant(key, want) || metaEntryMatches(key, entry, raw)) {
        index.set(want, key);
        return key;
      }
    }

    misses.add(want);
    return null;
  }

  function filterValueCandidates(filterName, value) {
    const list = [];
    const push = (item) => {
      if (item && !list.includes(item)) list.push(item);
    };

    push(value);

    const noFilter = String(filterName || '')
      .replace(/^filter\.p\.m\./i, '')
      .replace(/^filter\.(p|v)\./i, '');
    const tail = noFilter.split('.').pop();

    [noFilter, tail, stripMetaKeyNoise(noFilter), stripMetaKeyNoise(tail)].forEach((part) => {
      if (!part) return;
      push(`${part}/${value}`);
      push(`${part}:${value}`);
      push(`${part}.${value}`);
    });

    return list;
  }

  function resolveFriendlyKey(value, filterName) {
    const direct = resolveKeyFromValue(value, true);
    if (direct) return direct;

    const extras = filterValueCandidates(filterName, value).slice(1);
    for (let i = 0; i < extras.length; i++) {
      const hit = resolveKeyFromValue(extras[i], true);
      if (hit) return hit;
    }
    return null;
  }

  function isFriendlyValue(value) {
    return Boolean(resolveFriendlyKey(value));
  }

  function labelTextFor(input) {
    if (!input) return '';
    let label = input.labels && input.labels[0];
    if (!label) {
      const row = input.closest && input.closest('li, .collection__filter-checkbox');
      label = row && row.querySelector('label');
    }
    if (!label) return '';
    return String(label.textContent || '')
      .replace(/\s*\(\d+\)\s*$/, '')
      .trim();
  }

  function friendlySlugForMetaKey(key) {
    const entry = parseMetaEntry(getMetaObject()[key]);
    const fromCanonical = canonicalPathSlug(entry && entry.canonical_url);
    if (fromCanonical) return slugify(fromCanonical) || fromCanonical;
    return slugify((entry && (entry.slug || entry.handle)) || key);
  }

  function friendlySlugForInput(input) {
    if (!input) return '';

    const byValue = resolveFriendlyKey(input.value, input.name);
    if (byValue) return friendlySlugForMetaKey(byValue);

    const label = labelTextFor(input);
    if (label && !valuesMatch(label, input.value)) {
      const byLabel = resolveFriendlyKey(label, input.name);
      if (byLabel) return friendlySlugForMetaKey(byLabel);
    }
    return '';
  }

  function isNativeQueryFilter(name) {
    const n = String(name || '').toLowerCase();
    return n.includes('price') || n.includes('availability') || n.endsWith('.gte') || n.endsWith('.lte');
  }

  function activeSortQuery() {
    const sortBy = document.querySelector('[name="sort_by"]')?.value;
    if (!sortBy) return '';
    const defaultSort = api.defaultSortBy || 'manual';
    if (sortBy === defaultSort) return '';
    return `sort_by=${encodeURIComponent(sortBy)}`;
  }

  // ---------------------------------------------------------------------------
  // URL building — the single source of truth
  // ---------------------------------------------------------------------------

  function currentPathSlugs(url) {
    const parsed = new URL(url || window.location.href, window.location.origin);
    const base = collectionBaseUrl();
    if (!base || base === '/' || !parsed.pathname.toLowerCase().startsWith(`${base.toLowerCase()}/`)) return [];

    const remainder = parsed.pathname.slice(base.length).replace(/^\/+/, '');
    const segment = remainder.split('/')[0];
    if (!segment) return [];

    return segment
      .split('+')
      .map((slug) => slugify(decodeURIComponent(slug)))
      .filter(Boolean);
  }

  function getFilterForms() {
    return Array.prototype.slice.call(document.querySelectorAll(FILTER_FORM_SELECTOR));
  }

  function resolveFilterForm(input) {
    return (
      (input && input.closest && input.closest('form')) ||
      document.querySelector('#desktop-filters-form') ||
      document.querySelector('#mobile-collection-filters-form') ||
      getFilterForms()[0] ||
      null
    );
  }

  /**
   * Full filter state for a navigation:
   *   - the current URL (every MegaTree change is a real page load, so the URL
   *     is the authoritative record of what is applied right now)
   *   - plus the live state of the form the shopper just touched
   *   - minus the filter that was just toggled off
   * Friendly values become path slugs; everything else stays in the query.
   */
  function collectFilterState(input) {
    const parsed = new URL(window.location.href);
    const slugs = new Set(currentPathSlugs());
    const query = [];
    const extra = [];
    const seenQuery = new Set();
    const seenExtra = new Set();

    parsed.searchParams.forEach((value, key) => {
      if (!value || key === 'sort_by' || key.indexOf('filter.') !== 0) return;

      if (isNativeQueryFilter(key)) {
        upsertPair(extra, seenExtra, key, value);
        return;
      }

      // A ?filter.* value MegaTree has a friendly entry for belongs in the path.
      const slug = friendlySlugFromValue(value, key);
      if (slug) {
        slugs.add(slug);
        return;
      }
      upsertPair(query, seenQuery, key, value);
    });

    if (input && input.type === 'checkbox' && !input.checked) {
      // Just unchecked: drop it from whatever it currently lives in.
      const slug = friendlySlugForInput(input);
      if (slug) slugs.delete(slug);
      removePair(query, input.name, input.value);
      removePair(extra, input.name, input.value);
    }

    const form = resolveFilterForm(input);
    if (form) {
      form.querySelectorAll('input[type="checkbox"]').forEach((box) => {
        if (box.disabled) return;
        if ((box.id || '').indexOf('no-js') >= 0) return;
        const name = box.name || '';
        if (name.indexOf('filter') !== 0) return;
        if (!box.checked) return;

        if (isNativeQueryFilter(name)) {
          upsertPair(extra, seenExtra, name, box.value);
          return;
        }

        const slug = friendlySlugForInput(box);
        if (slug) {
          slugs.add(slug);
          return;
        }
        upsertPair(query, seenQuery, name, box.value);
      });

      form.querySelectorAll('input[name$=".gte"], input[name$=".lte"]').forEach((priceInput) => {
        const name = priceInput.name || '';
        if (name.indexOf('filter') !== 0) return;

        // The form is the live truth for price, so it replaces whatever the
        // current URL carries — including clearing it back to empty.
        removePairsByName(extra, name, seenExtra);

        const value = String(priceInput.value || '').trim();
        if (!value) return;
        upsertPair(extra, seenExtra, name, value);
      });
    }

    return { slugs: slugs, query: query, extra: extra };
  }

  function friendlySlugFromValue(value, filterName) {
    const key = resolveFriendlyKey(value, filterName);
    return key ? friendlySlugForMetaKey(key) : '';
  }

  /**
   * MegaTree can define a combined slug for a set of values (multi/multi_tags).
   * Only used when the metafield actually declares it.
   */
  function findMultiTag(slugs) {
    const meta = getMetaObject();
    const have = slugs.map((slug) => slugify(slug));
    let best = null;

    Object.keys(meta).forEach((key) => {
      const entry = parseMetaEntry(meta[key]);
      if (!entry || String(entry.multi) !== '1' || !Array.isArray(entry.multi_tags)) return;

      const subset = entry.multi_tags.every((tag) => {
        const slug = slugify(tag);
        return have.includes(slug) || have.some((h) => valuesMatch(h, tag));
      });
      if (subset && (!best || entry.multi_tags.length > best.tags.length)) {
        best = { tag: key, tags: entry.multi_tags };
      }
    });

    if (!best) return null;

    const slug = friendlySlugForMetaKey(best.tag);
    return { slug: slug, tags: best.tags };
  }

  function buildPathSegment(slugs) {
    const unique = Array.from(new Set(slugs.filter(Boolean)));
    if (!unique.length) return '';

    const multi = findMultiTag(unique);
    if (multi) {
      const rest = unique.filter((slug) => !multi.tags.some((tag) => valuesMatch(tag, slug)));
      return [multi.slug].concat(rest.sort()).join('+');
    }

    return unique.sort().join('+');
  }

  function buildFilterUrl(input) {
    const base = collectionBaseUrl();
    if (!base || base === '/') return '';

    const state = collectFilterState(input);
    const segment = buildPathSegment(Array.from(state.slugs));
    const path = segment ? `${base}/${segment}` : base;

    const parts = [];
    state.query.forEach(([name, value]) => {
      parts.push(`${encodeURIComponent(name)}=${encodeURIComponent(value)}`);
    });
    state.extra.forEach(([name, value]) => {
      parts.push(`${encodeURIComponent(name)}=${encodeURIComponent(value)}`);
    });

    const sort = activeSortQuery();
    if (sort) parts.push(sort);

    return parts.length ? `${path}?${parts.join('&')}` : path;
  }

  function collectionBaseUrlWithSort() {
    const base = collectionBaseUrl();
    const sort = activeSortQuery();
    return sort ? `${base}?${sort}` : base;
  }

  // ---------------------------------------------------------------------------
  // Interception — registered at file scope so it beats theme JS
  // ---------------------------------------------------------------------------

  function navigateTo(url) {
    if (navigating || !url) return;
    navigating = true;
    window.location.href = url;
  }

  function resolveCheckbox(target) {
    if (!target || !target.closest) return null;

    if (target.tagName === 'INPUT' && target.type === 'checkbox') return target;

    const direct = target.closest('input[type="checkbox"]');
    if (direct) return direct;

    const wrapper = target.closest('.checkbox-wrapper, .color-swatch, .collection__filter-checkbox');
    if (wrapper) {
      const nested = wrapper.querySelector('input[type="checkbox"]');
      if (nested) return nested;
    }

    const label = target.closest('label');
    if (!label) return null;

    if (label.htmlFor) {
      const byId = document.getElementById(label.htmlFor);
      if (byId && byId.type === 'checkbox') return byId;
    }
    return label.querySelector('input[type="checkbox"]');
  }

  function shouldTakeOver(input) {
    const onFriendlyPath = currentPathSlugs().length > 0;

    // Price / availability cannot be a path. But while a friendly path is
    // active they must still be handled here, or the theme AJAX would drop the
    // path segment when it rebuilds the query from the form.
    if (isNativeQueryFilter(input.name)) return onFriendlyPath;

    if (friendlySlugForInput(input)) return true;
    if (onFriendlyPath) return true;

    const form = resolveFilterForm(input);
    if (!form) return false;

    return Array.prototype.some.call(form.querySelectorAll('input[type="checkbox"]:checked'), (box) => {
      if (!box.name || box.name.indexOf('filter') !== 0) return false;
      if (isNativeQueryFilter(box.name)) return false;
      return isFriendlyValue(box.value);
    });
  }

  function handleFilterChange(input) {
    if (!input || input.disabled) return false;
    if (!input.name || input.name.indexOf('filter') !== 0) return false;
    if (!shouldTakeOver(input)) return false;

    const url = buildFilterUrl(input);
    if (!url) return false;

    navigateTo(url);
    return true;
  }

  /**
   * Legacy ?filter.* URLs that MegaTree has friendly entries for become real
   * path URLs so the canonical stays a single address.
   */
  function rewriteGroupQuery() {
    if (api._rewriting) return false;

    const base = collectionBaseUrl();
    if (!base || base === '/') return false;
    if (!window.location.pathname.includes('/collections/')) return false;

    const params = new URLSearchParams(window.location.search);
    const slugs = new Set(currentPathSlugs());
    const keep = new URLSearchParams();
    const defaultSort = api.defaultSortBy || 'manual';
    let promoted = false;

    params.forEach((value, key) => {
      if (!value) return;
      if (key === 'sort_by' && value === defaultSort) return;

      if (key.indexOf('filter.') === 0 && !isNativeQueryFilter(key)) {
        const slug = friendlySlugFromValue(value, key);
        if (slug) {
          slugs.add(slug);
          promoted = true;
          return;
        }
      }
      keep.append(key, value);
    });

    if (!promoted) return false;

    const segment = buildPathSegment(Array.from(slugs));
    let next = segment ? `${base}/${segment}` : base;
    const qs = keep.toString();
    if (qs) next += `?${qs}`;

    const current = window.location.pathname + window.location.search;
    if (next === current) return false;

    api._rewriting = true;
    window.location.replace(next);
    return true;
  }

  function patchHistory() {
    const push = history.pushState;
    const replace = history.replaceState;

    history.pushState = function () {
      const result = push.apply(this, arguments);
      rewriteGroupQuery();
      return result;
    };
    history.replaceState = function () {
      const result = replace.apply(this, arguments);
      rewriteGroupQuery();
      return result;
    };
  }

  function initClickOverrides() {
    document.addEventListener(
      'click',
      (event) => {
        if (!event.target || !event.target.closest) return;

        if (event.target.closest('[name^="subcollection"], input[name^="subcollection"]')) return;

        const clearAll = event.target.closest('[data-megatree-clear-all]');
        if (clearAll) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          navigateTo(clearAll.getAttribute('data-url') || clearAll.getAttribute('href') || collectionBaseUrlWithSort());
          return;
        }

        // Prestige "Clear all" lives in the sidebar card and the drawer header
        // (the latter is outside the form), so scope by class, not by form id.
        // A single-value pill uses .collection__active-filter-item instead and
        // must keep the path slugs it is not removing.
        const prestigeClear = event.target.closest('[data-action="clear-filters"]');
        if (prestigeClear && currentPathSlugs().length) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          const target = prestigeClear.getAttribute('data-url') || prestigeClear.getAttribute('href') || '';
          let query = '';
          try {
            query = new URL(target || collectionBaseUrl(), window.location.origin).search.replace(/^\?/, '');
          } catch (e) {
            query = '';
          }

          const isClearAll =
            prestigeClear.classList.contains('collection__clear-filter') ||
            prestigeClear.hasAttribute('data-megatree-clear-all') ||
            Boolean(prestigeClear.closest('.collection-drawer__header'));
          if (isClearAll) {
            navigateTo(query ? `${collectionBaseUrl()}?${query}` : collectionBaseUrlWithSort());
            return;
          }

          const segment = buildPathSegment(currentPathSlugs());
          navigateTo(segment ? `${collectionBaseUrl()}/${segment}${query ? `?${query}` : ''}` : collectionBaseUrlWithSort());
          return;
        }

        const removeTag = event.target.closest('[data-megatree-remove-tag]');
        if (removeTag) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          removeTagFromPage(removeTag);
        }
      },
      true
    );
  }

  function removeTagFromPage(pill) {
    const tag = pill.getAttribute('data-megatree-remove-tag');
    const remaining = currentPathSlugs().filter((slug) => !valuesMatch(slug, tag));
    const base = collectionBaseUrl();

    if (!remaining.length) {
      navigateTo(collectionBaseUrlWithSort());
      return;
    }

    // Uncheck the value in every form so the new page state is consistent.
    document.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      if (input.name && input.name.indexOf('filter') === 0 && valuesMatch(input.value, tag)) {
        input.checked = false;
      }
    });

    const sort = activeSortQuery();
    navigateTo(`${base}/${buildPathSegment(remaining)}${sort ? `?${sort}` : ''}`);
  }

  // ---------------------------------------------------------------------------
  // DOM hydrate — replaces surgical filter Liquid
  // ---------------------------------------------------------------------------

  function initHydrate() {
    hydrateAll();

    const observeRoots = [
      document.querySelector('[data-collection-products]'),
      document.querySelector('#desktop-filters-form .collection__filter-group-list'),
      document.querySelector('#mobile-collection-filters-form'),
      document.querySelector('collection-filters-form'),
      document.querySelector('.facets-container .facets__form'),
      document.querySelector('#FacetsWrapperDesktop')
    ].filter(Boolean);

    observeRoots.forEach((root) => {
      const observer = new MutationObserver((mutations) => {
        if (hydrating || suppressHydrate) return;
        const selfOnly = mutations.every((m) => {
          const target = m.target;
          if (!(target instanceof Element)) return false;
          return Boolean(
            target.closest('[data-megatree-pills], [data-megatree-pills-host], #MegaTreeActiveFilters, .megatree-active-filters')
          );
        });
        if (selfOnly) return;
        queueHydrate();
      });
      observer.observe(root, { childList: true, subtree: true });
    });
  }

  function queueHydrate() {
    if (hydrating || suppressHydrate) return;
    clearTimeout(hydrateTimer);
    hydrateTimer = setTimeout(hydrateAll, 80);
  }

  function hydrateAll() {
    if (hydrating) return;
    hydrating = true;
    suppressHydrate = true;
    try {
      syncCurrentTagsFromUrl();
      hydrateCheckboxes();
      hydrateLabels();
      hydrateAccordions();
      injectPathTagPills();
    } finally {
      hydrating = false;
      setTimeout(() => {
        suppressHydrate = false;
      }, 120);
    }
  }

  function syncCurrentTagsFromUrl() {
    api.currentTags = Array.isArray(api.currentTags) ? api.currentTags : [];

    const parsed = new URL(window.location.href);
    const base = collectionBaseUrl();
    if (!base || base === '/') return;

    if (parsed.pathname.toLowerCase().startsWith(`${base.toLowerCase()}/`)) {
      const remainder = parsed.pathname.slice(base.length).replace(/^\/+/, '');
      const segment = remainder.split('/')[0];
      if (segment) {
        const tags = segment
          .split('+')
          .map((slug) => decodeURIComponent(slug).trim())
          .filter(Boolean);
        if (tags.length) {
          api.currentTags = tags;
          return;
        }
      }
    }

    if (parsed.pathname === base || parsed.pathname === `${base}/`) api.currentTags = [];
  }

  function hydrateCheckboxes() {
    const currentTags = Array.isArray(api.currentTags) ? api.currentTags : [];
    if (!currentTags.length) return;

    // One checkbox per path tag *per form*: desktop and mobile mirror each
    // other, but within one form a "Brand" and a "Brand." row are duplicates.
    getFilterForms().forEach((form) => {
      const claimed = new Set();

      form.querySelectorAll('input[type="checkbox"][name^="filter"]').forEach((input) => {
        if ((input.id || '').indexOf('no-js') >= 0) return;
        if (isNativeQueryFilter(input.name)) return;

        const matchedTag = currentTags.find((tag) => valuesMatch(tag, input.value));
        if (!matchedTag) return;

        const claimKey = slugify(matchedTag);
        const shouldCheck = !claimed.has(claimKey);
        if (shouldCheck) claimed.add(claimKey);

        if (shouldCheck && !input.checked) {
          input.checked = true;
        } else if (!shouldCheck && input.checked && isFriendlyValue(input.value)) {
          input.checked = false;
        }

        const item =
          input.closest('.sidebar__item, .facets__item, .mobile-facets__item, .collection__filter-checkbox, li, .filter-group__item') ||
          (input.closest('label') && input.closest('label').parentElement);
        if (!item || !shouldCheck) return;

        item.classList.add('sidebar__item--active', 'link--remove');
        item.classList.remove('link--add');
      });
    });
  }

  function hydrateLabels() {
    const meta = getMetaObject();

    document.querySelectorAll('input[type="checkbox"][name^="filter"]').forEach((input) => {
      const key = resolveKeyFromValue(input.value);
      const entry = key ? parseMetaEntry(meta[key]) : null;
      if (!entry || !entry.name) return;

      const label =
        input.closest('label') ||
        (input.id
          ? (() => {
              try {
                return document.querySelector(`label[for="${CSS.escape(input.id)}"]`);
              } catch (e) {
                return null;
              }
            })()
          : null);
      if (!label) return;

      const textNode =
        label.querySelector('span:not(.visually-hidden):not([aria-hidden="true"])') ||
        Array.prototype.slice
          .call(label.querySelectorAll('span'))
          .find((s) => s.textContent.trim() && !s.querySelector('svg'));

      if (textNode && textNode.childElementCount === 0) {
        const countMatch = textNode.textContent.match(/\s*\(\d+\)\s*$/);
        textNode.textContent = countMatch ? `${entry.name}${countMatch[0]}` : entry.name;
      }
    });
  }

  function hydrateAccordions() {
    const currentTags = Array.isArray(api.currentTags) ? api.currentTags : [];
    if (!currentTags.length) return;

    document.querySelectorAll('input[type="checkbox"][name^="filter"]:checked').forEach((input) => {
      if (!tagsInclude(currentTags, input.value) && !isFriendlyValue(input.value)) return;

      const details = input.closest('details');
      if (details && !details.open) details.open = true;

      const body = input.closest('[data-collapsible-body]');
      if (body) body.style.height = 'auto';
    });
  }

  const CLOSE_SVG =
    '<svg focusable="false" class="icon icon--close" viewBox="0 0 19 19" role="presentation">' +
    '<path d="M9.1923882 8.39339828l7.7781745-7.7781746 1.4142136 1.41421357-7.7781746 7.77817459 7.7781746 7.77817456L16.9705627 19l-7.7781745-7.7781746L1.41421356 19 0 17.5857864l7.7781746-7.77817456L0 2.02943725 1.41421356.61522369 9.1923882 8.39339828z" fill="currentColor" fill-rule="evenodd"></path>' +
    '</svg>';

  function injectPathTagPills() {
    const currentTags = Array.isArray(api.currentTags) ? api.currentTags : [];
    const clearHref = collectionBaseUrlWithSort();
    const pillsKey = currentTags
      .map((t) => slugify(t))
      .filter(Boolean)
      .sort()
      .join('+');

    const hosts = resolveActiveFiltersHosts();
    if (!hosts.length) return;

    hosts.forEach((host) => {
      if (!currentTags.length) {
        host.querySelectorAll('[data-megatree-remove-tag], [data-megatree-clear-all]').forEach((el) => el.remove());
        host.removeAttribute('data-megatree-pills-key');
        return;
      }

      const existingKey = Array.prototype.slice
        .call(host.querySelectorAll('[data-megatree-remove-tag]'))
        .map((el) => slugify(el.getAttribute('data-megatree-remove-tag')))
        .filter(Boolean)
        .sort()
        .join('+');

      if (existingKey === pillsKey && host.getAttribute('data-megatree-pills-key') === pillsKey) {
        syncClearHref(host, clearHref);
        return;
      }

      host.setAttribute('data-megatree-pills-key', pillsKey);
      host.querySelectorAll('[data-megatree-remove-tag], [data-megatree-clear-all]').forEach((el) => el.remove());

      const nodes = currentTags.map((tag) => {
        const a = document.createElement('a');
        a.href = collectionBaseUrl();
        a.className = 'collection__active-filter-item';
        a.setAttribute('data-megatree-remove-tag', tag);
        a.innerHTML = `<span class="collection__active-filter-cross">${CLOSE_SVG}</span>` + escapeHtml(getDisplayLabel(tag));
        return a;
      });

      const clearBtn = host.querySelector('.collection__clear-filter[data-action="clear-filters"]');
      if (clearBtn) {
        clearBtn.setAttribute('data-url', clearHref);
        nodes.forEach((node) => host.insertBefore(node, clearBtn));
        return;
      }

      nodes.forEach((node) => host.appendChild(node));

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'collection__clear-filter button button--small button--transparent button--full';
      button.setAttribute('data-megatree-clear-all', '1');
      button.setAttribute('data-url', clearHref);
      button.textContent = 'Clear all';
      host.appendChild(button);
    });
  }

  function syncClearHref(host, clearHref) {
    const prestigeClear = host.querySelector('.collection__clear-filter[data-action="clear-filters"], [data-megatree-clear-all]');
    if (!prestigeClear) return;
    if (prestigeClear.hasAttribute('data-url')) prestigeClear.setAttribute('data-url', clearHref);
    if (prestigeClear.tagName === 'A') prestigeClear.setAttribute('href', clearHref);
  }

  function resolveActiveFiltersHosts() {
    // Optional per-theme override from boot or console:
    // window.ShopifyMegatree.activeFiltersSelector = '.my-theme-active-filters'
    const custom = api.activeFiltersSelector;
    if (custom) {
      const found = Array.prototype.slice.call(document.querySelectorAll(custom));
      if (found.length) return found;
    }

    const candidates = [
      '#desktop-filters-form .collection__active-filters',
      '#mobile-collection-filters .collection__active-filters',
      '#mobile-collection-filters-form .collection__active-filters',
      '.collection__active-filters',
      '.active-facets-desktop',
      '.active-facets',
      '.active-facets-mobile',
      'facet-filters-form .active-facets',
      '[data-collection-active-filters]',
      '.facets__selected',
      '.filter-active'
    ];

    const hosts = [];
    candidates.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => {
        if (!hosts.includes(el)) hosts.push(el);
      });
    });

    if (hosts.length) return hosts;
    return [createActiveFiltersHost()].filter(Boolean);
  }

  function createActiveFiltersHost() {
    let host = document.querySelector('#MegaTreeActiveFilters');
    if (host) return host;

    const mount =
      document.querySelector('#desktop-filters-form') ||
      document.querySelector(FILTER_FORM_SELECTOR) ||
      document.querySelector(SIDEBAR_ROOT_SELECTOR) ||
      document.querySelector('main .collection, main [id*="collection"], main');

    if (!mount) return null;

    host = document.createElement('div');
    host.id = 'MegaTreeActiveFilters';
    host.className = 'megatree-active-filters collection__active-filters';
    host.setAttribute('data-megatree-pills-host', '1');
    host.setAttribute('data-megatree-active-filters', '1');

    const before =
      mount.querySelector('.collection__filter-group-list, .facets__form, .facet-filters, [class*="filter-group"]') ||
      mount.querySelector('fieldset, details.js-filter, .facets__disclosure');

    if (before && before.parentElement) {
      before.parentElement.insertBefore(host, before);
    } else {
      mount.prepend(host);
    }

    return host;
  }

  // ---------------------------------------------------------------------------
  // H1 / labels
  // ---------------------------------------------------------------------------

  function getDisplayLabel(value) {
    const meta = getMetaObject();
    const direct = parseMetaEntry(meta[value]);
    if (direct && direct.name) return direct.name;

    const key = resolveKeyFromValue(value, false);
    if (key && meta[key] && meta[key].name) return meta[key].name;

    return decodeURIComponent(String(value || '').replace(/\+/g, ' ')).trim();
  }

  function getActiveFilterLabels(url) {
    const labels = [];
    const seen = new Set();
    const parsed = new URL(url, window.location.origin);

    const add = (label) => {
      const key = slugify(label);
      if (!key || seen.has(key)) return;
      seen.add(key);
      labels.push(label);
    };

    currentPathSlugs(parsed).forEach((slug) => add(getDisplayLabel(slug)));

    parsed.searchParams.forEach((value, key) => {
      if (key.indexOf('filter.') !== 0) return;
      if (isNativeQueryFilter(key) || !value) return;
      add(getDisplayLabel(value));
    });

    if (!labels.length) {
      document
        .querySelectorAll('input[type="checkbox"][name^="filter"]:checked')
        .forEach((input) => add(getDisplayLabel(input.value)));
    }

    return labels;
  }

  function getRawTags(url) {
    const parsed = new URL(url, window.location.origin);
    const base = collectionBaseUrl();

    if (base && base !== '/' && parsed.pathname.toLowerCase().startsWith(`${base.toLowerCase()}/`)) {
      const remainder = parsed.pathname.slice(base.length).replace(/^\/+/, '');
      const segment = remainder.split('/')[0];
      if (segment) {
        return segment
          .split('+')
          .map((slug) => decodeURIComponent(slug).trim())
          .filter(Boolean);
      }
    }

    return Array.isArray(api.currentTags) ? api.currentTags.filter(Boolean) : [];
  }

  function buildH1Title(url) {
    const meta = getMetaObject();
    const tags = getRawTags(url || window.location.href);

    if (tags.length) {
      const entry =
        parseMetaEntry(meta[tags.join('+')]) ||
        (resolveKeyFromValue(tags.join('+'), false) ? parseMetaEntry(meta[resolveKeyFromValue(tags.join('+'), false)]) : null) ||
        (tags[0] ? parseMetaEntry(meta[resolveKeyFromValue(tags[0], false) || tags[0]]) : null);
      if (entry && entry.h1) return String(entry.h1).trim();
    }

    const baseTitle = api.collectionTitle || '';
    const labels = getActiveFilterLabels(url || window.location.href);
    if (!labels.length) return baseTitle;
    return `${labels.join(' + ')} ${baseTitle}`.trim();
  }

  function syncH1FromUrl() {
    syncCurrentTagsFromUrl();

    const nextTitle = buildH1Title();
    const headings = document.querySelectorAll(H1_SELECTOR);

    headings.forEach((h1) => {
      let updated = false;

      for (const node of h1.childNodes) {
        if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
          node.textContent = ` ${nextTitle} `;
          updated = true;
          break;
        }
      }

      if (!updated) h1.insertBefore(document.createTextNode(` ${nextTitle} `), h1.firstChild);
    });
  }

  function initH1Sync() {
    syncH1FromUrl();

    window.addEventListener('popstate', syncH1FromUrl);
    window.addEventListener('pageshow', syncH1FromUrl);

    const observeRoots = [
      document.querySelector('[data-collection-products]'),
      document.querySelector('collection-filters-form'),
      document.querySelector('.collection__sidebar'),
      document.querySelector('.collection-image-with-title'),
      document.querySelector('.collection-hero')
    ].filter(Boolean);

    observeRoots.forEach((root) => {
      const observer = new MutationObserver(() => queueH1Sync());
      observer.observe(root, { childList: true, subtree: true });
    });
  }

  function queueH1Sync() {
    clearTimeout(h1SyncTimer);
    h1SyncTimer = setTimeout(syncH1FromUrl, 50);
  }

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------

  api.meta = api.meta || {};
  api.currentTags = Array.isArray(api.currentTags) ? api.currentTags : [];
  api.url = String(api.url || '').replace(/\/+$/, '');

  // Exposed before any DOM work: theme.js reads these on the first filter click.
  api.friendlySlugForInput = friendlySlugForInput;
  api.handleFilterChange = handleFilterChange;
  api.resolveFriendlyKey = resolveFriendlyKey;
  api.isFriendlyEntry = isFriendlyEntry;
  api.buildFilterUrl = buildFilterUrl;
  api.shouldTakeOver = shouldTakeOver;
  api.syncH1 = syncH1FromUrl;
  api.buildH1Title = buildH1Title;
  api.hydrate = hydrateAll;

  // Capture phase on window + document so this runs before the theme's own
  // bubble-phase filter handler, with or without stopPropagation in between.
  ['change'].forEach((type) => {
    window.addEventListener(
      type,
      (event) => {
        const input = resolveCheckbox(event.target) || event.target;
        if (!input || input.tagName !== 'INPUT') return;
        if (handleFilterChange(input)) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
        }
      },
      true
    );

    document.addEventListener(
      type,
      (event) => {
        const input = resolveCheckbox(event.target) || event.target;
        if (!input || input.tagName !== 'INPUT') return;
        if (handleFilterChange(input)) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
        }
      },
      true
    );
  });

  patchHistory();
  initClickOverrides();
  rewriteGroupQuery();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      api.collectionTitle =
        api.collectionTitle ||
        (document.querySelector(
          '.collection-image-with-title h1.hero__title, h1.collection-hero__title, h1.collection__title, h1.hero__title, .collection-hero h1, main h1'
        )?.childNodes[0]?.textContent || '').trim() ||
        '';

      rebuildIndexes();
      initHydrate();
      initH1Sync();
    });
  } else {
    api.collectionTitle = api.collectionTitle || '';
    rebuildIndexes();
    initHydrate();
    initH1Sync();
  }
})();
